import {
    TextField,
    Box,
    Grid,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    Checkbox,
    Button,
    FormLabel,
    Typography,
    InputLabel,
    FormHelperText,
    FormGroup,
    Link,
    OutlinedInput,
    useMediaQuery,
    Theme,
} from '@mui/material'
import { useRef, type FC } from 'react'
import { Step1Props } from '../types'
import { Controller } from 'react-hook-form'
import HelperModal from '../../../hoc/HelperModal'
import { useGtmPageView } from '../../hooks'
import { AvatarPlayer } from '../../common'
import { useActivationObjectUpdateMutation } from '../../../../api/activationObject/hooks'
import { PersonType } from '../../../providers/ActivationObjectProvider/types'

const Step1View: FC<Step1Props> = (props) => {
    const { form, isUpdating, activationObject, buttonActions } = props
    const updateMutation = useActivationObjectUpdateMutation()
    const { control, formState, handleSubmit, setValue } = form.form
    const { errors } = formState
    const isMobile = useMediaQuery((theme: Theme) =>
        theme.breakpoints.between('xs', 'md'),
    )
    const isTablet = useMediaQuery((theme: Theme) =>
        theme.breakpoints.between('md', 'lg'),
    )

    const isPortable = isMobile || isTablet

    useGtmPageView({ pageName: 'Step1', synonym: activationObject?.synonym })

    const formRef = useRef<HTMLFormElement>(null)

    // const handleFormSubmit = async (successFunction: () => void) => {
    //     return new Promise(async (resolve, reject) => {
    //         const result = await handleSubmit((data) =>
    //             form.onSubmit(data, successFunction),
    //         )()
    //         resolve(result)
    //     })
    // }

    const handleFormSubmit = async (successFunction: () => void) => {
        await handleSubmit((data) => form.onSubmit(data, successFunction))()
    }

    return (
        <form
            ref={formRef}
            id='step1form'
        >
            <Grid container>
                <Grid
                    item
                    xs={12}
                    lg={7}
                    spacing={3}
                    pb={{ xs: 2, md: 5 }}
                >
                    <Box className='pb-8 md:pb-14'>
                        <Typography
                            className='font-bold text-2xl pb-4 md:text-4xl md:pb-8'
                            variant='h1'
                        >
                            Základní informace
                        </Typography>
                        <Typography
                            variant='body1'
                            paragraph
                            className='font-thin pb-8 lg:pb-0'
                        >
                            Pro pokračování potřebujeme nejprve ověřit, že jste
                            oprávněn/a uzavřít smlouvu a potvrdit si komunikační
                            údaje.
                        </Typography>
                        {isPortable && (
                            <AvatarPlayer
                                className='lg:hidden'
                                videoName='SimpleInfo.mp4'
                            />
                        )}
                    </Box>
                    <Grid
                        container
                        spacing={{ xs: 2, md: 3 }}
                    >
                        <Grid
                            item
                            xs={12}
                        >
                            <FormControl
                                variant='outlined'
                                fullWidth
                            >
                                <InputLabel htmlFor='competentPerson'>
                                    Zákazník
                                </InputLabel>
                                <Controller
                                    name={'personName'}
                                    control={control}
                                    render={({ field }) => (
                                        <OutlinedInput
                                            {...field}
                                            id='competentPerson'
                                            label='Zákazník'
                                            fullWidth
                                            className='rounded-md'
                                            aria-errormessage='competentPerson-error-message'
                                            endAdornment={
                                                <HelperModal textKey='AUTHORIZED_PERSON' />
                                            }
                                            error={
                                                errors.personName !== undefined
                                            }
                                        />
                                    )}
                                />
                                <FormHelperText
                                    id='competentPerson-error-message'
                                    className='text-red-600'
                                >
                                    {errors.personName?.message}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid
                            item
                            container
                            sm={6}
                        >
                            <Controller
                                name={'personPhoneNumber'}
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        id='phone'
                                        {...field}
                                        fullWidth
                                        label='Telefonní číslo'
                                        variant='outlined'
                                        className='rounded-md'
                                        error={
                                            errors.personPhoneNumber !==
                                            undefined
                                        }
                                        helperText={
                                            errors.personPhoneNumber?.message
                                        }
                                    />
                                )}
                            />
                        </Grid>
                        <Grid
                            item
                            container
                            sm={6}
                        >
                            <Controller
                                name={'personEmail'}
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        id='email'
                                        {...field}
                                        fullWidth
                                        variant='outlined'
                                        label='E-mail'
                                        error={errors.personEmail !== undefined}
                                        helperText={errors.personEmail?.message}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            md={6}
                        >
                            <FormControl>
                                <FormLabel>Právní forma</FormLabel>
                                <Controller
                                    name={'personType'}
                                    control={control}
                                    render={({ field }) => (
                                        <RadioGroup
                                            row
                                            aria-label='personType'
                                            {...field}
                                            onChange={(e) => {
                                                field.onChange(e)
                                                if (activationObject) {
                                                    const {
                                                        actualWizardStep,
                                                        previousWizardStep,
                                                        ...rest
                                                    } = activationObject
                                                    updateMutation.mutate({
                                                        ...rest,
                                                        personType: e.target
                                                            .value as PersonType,
                                                    })
                                                }
                                            }}
                                        >
                                            <FormControlLabel
                                                value='Physical'
                                                control={
                                                    <Radio
                                                        size='small'
                                                        onClick={() =>
                                                            setValue(
                                                                'personAuthorisationType',
                                                                'Owener',
                                                            )
                                                        }
                                                        color='primary'
                                                    />
                                                }
                                                componentsProps={{
                                                    typography: {
                                                        fontSize: '0.875rem',
                                                    },
                                                }}
                                                label='Fyzická osoba'
                                            />
                                            <FormControlLabel
                                                value='Legal'
                                                control={
                                                    <Radio
                                                        size='small'
                                                        onClick={() =>
                                                            setValue(
                                                                'personAuthorisationType',
                                                                'StatutoryAuthority',
                                                            )
                                                        }
                                                        color='primary'
                                                    />
                                                }
                                                componentsProps={{
                                                    typography: {
                                                        fontSize: '0.875rem',
                                                    },
                                                }}
                                                label='Právnická osoba'
                                            />
                                        </RadioGroup>
                                    )}
                                />
                            </FormControl>
                        </Grid>
                        {/* <Grid
                            item
                            xs={12}
                            md={6}
                        >
                            <FormControl fullWidth>
                                <InputLabel id='personAuthorisationType'>
                                    Druh oprávnění
                                </InputLabel>
                                <Controller
                                    name={'personAuthorisationType'}
                                    control={control}
                                    defaultValue={
                                        isPhysical
                                            ? 'Owener'
                                            : 'StatutoryAuthority'
                                    }
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            id='personAuthorisationType'
                                            value={personAuthorization}
                                            label='Druh oprávnění'
                                            variant='outlined'
                                            className='rounded-md'
                                            error={
                                                errors.personAuthorisationType !==
                                                undefined
                                            }
                                        >
                                            {options.permissions
                                                .filter((option) =>
                                                    isPhysical
                                                        ? option.isPhysical ===
                                                          true
                                                        : option.isPhysical ===
                                                          false,
                                                )
                                                .map((option) => (
                                                    <MenuItem
                                                        key={option.value}
                                                        value={option.value}
                                                    >
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    )}
                                />
                                {errors.personAuthorisationType?.message && (
                                    <FormHelperText>
                                        {
                                            errors.personAuthorisationType
                                                ?.message
                                        }
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </Grid> */}

                        {/* {conditions.showAnotherAuthorisationType && (
                            <>
                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                ></Grid>
                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                >
                                    <Controller
                                        name={'anotherAuthorisationType'}
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label='Jiný druh oprávnění'
                                                variant='outlined'
                                                className='rounded-md'
                                                error={
                                                    errors.anotherAuthorisationType !==
                                                    undefined
                                                }
                                                helperText={
                                                    errors
                                                        .anotherAuthorisationType
                                                        ?.message
                                                }
                                            ></TextField>
                                        )}
                                    />
                                </Grid>
                            </>
                        )}

                        {conditions.showUploadFile && (
                            <Grid
                                container
                                item
                                spacing={7}
                            >
                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                ></Grid>
                                <Grid
                                    container
                                    item
                                    spacing={2}
                                    xs={12}
                                    md={6}
                                >
                                    <Button
                                        component='label'
                                        variant='contained'
                                        className='normal-case font-regular shadow-none rounded-md'
                                        fullWidth
                                        endIcon={<CloudUpload />}
                                    >
                                        Nahrajte soubor o plné moci
                                        <Controller
                                            name={'uploadedFile'}
                                            control={control}
                                            render={({ field }) => (
                                                <input
                                                    type='file'
                                                    ref={file.fileRef}
                                                    onChange={
                                                        file.handleFileChange
                                                    }
                                                    style={{
                                                        clip: 'rect(0 0 0 0)',
                                                        clipPath: 'inset(50%)',
                                                        height: 1,
                                                        overflow: 'hidden',
                                                        position: 'absolute',
                                                        bottom: 0,
                                                        left: 0,
                                                        whiteSpace: 'nowrap',
                                                        width: 1,
                                                    }}
                                                    accept='.pdf, .jpg, .jpeg'
                                                />
                                            )}
                                        />
                                    </Button>
                                    {activationObject?.uploadedFile && (
                                        <Alert
                                            severity='info'
                                            className='w-full rounded-md'
                                            sx={{ marginY: 1 }}
                                        >
                                            Příloha vložena
                                        </Alert>
                                    )}
                                    {file.showFileError && (
                                        <Alert
                                            severity='warning'
                                            variant='outlined'
                                            className='w-full rounded-md'
                                        >
                                            {`Chyba při nahrávani souboru: ${file.resultMessage}`}
                                        </Alert>
                                    )}
                                </Grid>
                            </Grid>
                        )} */}
                        <Grid
                            container
                            item
                            xs={12}
                            spacing={1}
                            className='pb-4'
                        >
                            <Grid item>
                                <FormControl error={!!errors.check1?.message}>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Controller
                                                    name={'check1'}
                                                    control={form.form.control}
                                                    render={({ field }) => (
                                                        <Checkbox
                                                            {...field}
                                                            checked={
                                                                field.value
                                                            }
                                                            size='small'
                                                            color='primary'
                                                        />
                                                    )}
                                                />
                                            }
                                            componentsProps={{
                                                typography: {
                                                    fontSize: isMobile
                                                        ? '0.875rem'
                                                        : '1rem',
                                                },
                                            }}
                                            label={
                                                <Typography>
                                                    Seznámil/a jsem se se{' '}
                                                    <Link
                                                        rel='noreferrer'
                                                        underline='always'
                                                        target='_blank'
                                                        href='https://www.bezpecnostnicentrum.cz/file/edee/2025/02/f-18-09-zasady-zpracovani-osobnich-udaju.pdf'
                                                        color='inherit'
                                                    >
                                                        Zásadami zpracování
                                                        osobních
                                                    </Link>{' '}
                                                    údajů a jejich obsahu
                                                    rozumím
                                                </Typography>
                                            }
                                        />
                                        {!!errors.check1?.message && (
                                            <FormHelperText>
                                                {errors.check1?.message}
                                            </FormHelperText>
                                        )}
                                    </FormGroup>
                                </FormControl>
                            </Grid>
                            <Grid item>
                                <FormControl error={!!errors.check2?.message}>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Controller
                                                    name={'check2'}
                                                    control={form.form.control}
                                                    render={({ field }) => (
                                                        <Checkbox
                                                            {...field}
                                                            checked={
                                                                field.value
                                                            }
                                                            size='small'
                                                            color='primary'
                                                        />
                                                    )}
                                                />
                                            }
                                            componentsProps={{
                                                typography: {
                                                    fontSize: isMobile
                                                        ? '0.875rem'
                                                        : '1rem',
                                                },
                                            }}
                                            label={
                                                <Typography>
                                                    Seznámil/a jsem se s{' '}
                                                    <Link
                                                        rel='noreferrer'
                                                        underline='always'
                                                        target='_blank'
                                                        href='https://www.bezpecnostnicentrum.cz/file/edee/2020/11/f-17-10-03_vseobecne-obchodni-podminky_ucinne-od-31_10_2020.pdf'
                                                        color='inherit'
                                                    >
                                                        Všeobecnými obchodními
                                                        podmínkami
                                                    </Link>{' '}
                                                    a jejich obsahu rozumím
                                                </Typography>
                                            }
                                        />
                                        {!!errors.check2?.message && (
                                            <FormHelperText>
                                                {errors.check2?.message}
                                            </FormHelperText>
                                        )}
                                    </FormGroup>
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Grid
                            container
                            item
                            xs={12}
                            spacing={{ xs: '12px', lg: 3 }}
                        >
                            <Grid
                                item
                                xs={12}
                                lg={6}
                            >
                                <Button
                                    variant='outlined'
                                    color='secondary'
                                    size='large'
                                    className='normal-case text-[0.9rem] font-semibold lg:font-normal lg:text-xl rounded-md'
                                    fullWidth={!isPortable}
                                    disabled={isUpdating}
                                    onClick={(e) => {
                                        e.preventDefault()
                                        handleFormSubmit(
                                            buttonActions.helpProtection,
                                        )
                                    }}
                                >
                                    Chci pomoct s výběrem služby
                                </Button>
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                lg={6}
                            >
                                <Button
                                    variant='contained'
                                    color='primary'
                                    size='large'
                                    className='normal-case text-[0.9rem] font-semibold lg:font-normal lg:text-xl shadow-none rounded-md'
                                    fullWidth={!isPortable}
                                    disabled={isUpdating}
                                    onClick={(e) => {
                                        e.preventDefault()
                                        handleFormSubmit(
                                            buttonActions.continueFullProtection,
                                        )
                                    }}
                                >
                                    Pokračovat s celkovou ochranou
                                </Button>
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                lg={6}
                                className='md:mt-6 lg:mt-0'
                            >
                                <Link
                                    component='button'
                                    className='normal-case text-pantone-black-70 text-xs md:text-[1rem]'
                                    color='inherit'
                                    onClick={(e) => {
                                        e.preventDefault()
                                        buttonActions.callMeProtection()
                                    }}
                                >
                                    Kontaktujte mě prosím telefonicky
                                </Link>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    item
                    md={1}
                    display={{ xs: 'none', md: 'block' }}
                ></Grid>
                <Grid
                    item
                    xs={0}
                    lg={4}
                    className='hidden lg:block'
                >
                    {!isPortable && <AvatarPlayer videoName='SimpleInfo.mp4' />}
                </Grid>
            </Grid>
        </form>
    )
}

export default Step1View
